import { vec2, vec3 } from "gl-matrix"
import { applyTransform, rgbToHsv } from "./color-space"
import { ColorCluster, getClusterColorsAndWeights, getWeightedClusterCenter } from "./point-color"
import {
	getDecreasedValue,
	getWeightedMeanRgb,
	logColorMatch,
	mix,
	ReferenceColorMatchFeatures,
	step,
} from "./utils"
import { clamp } from "@/utils/math"
import { hueShiftScaleMap } from "@/core/hsl"
import { Adjustments } from "@/core/types"
import { lightKeys } from "../calibration/ai-calibration-feature"
import {
	percentDifference,
	absoluteDifference,
	extractKeys,
} from "../calibration/ai-calibration-utils"
import { enableVegetationCorrection } from "./parameters"
import { AsShotWB, RenderOptions, renderCpu, ColorSpaceCpu } from "./renderer-cpu"

/** Extract vegetation pixels (hue 45–160°, sat/val > 0.2) and return as a weighted color cluster. */
export function getVegetationCluster(result: {
	colors: Float32Array
	pixelWeights: number[]
	minHue?: number
}) {
	const { colors, pixelWeights, minHue = 45 } = result
	const hsvs = applyTransform(colors, rgbToHsv)
	const greenColors: vec3[] = []
	const greenWeights: number[] = []
	const greenIndices: number[] = []
	let weightSum = 0
	for (let i = 0; i < hsvs.length / 3; i++) {
		const h = hsvs[i * 3] as number
		const s = hsvs[i * 3 + 1] as number
		const v = hsvs[i * 3 + 2] as number
		const hue = h * 360
		// if (hue > minHue && hue < 160) {
		// 	logColorMatch("[getVegetationColors] pixel", [hue, s, v], pixelWeights[i])
		// }
		if (hue > minHue && hue < 160 && s > 0.2 && v > 0.2) {
			const r = colors[i * 3] as number
			const g = colors[i * 3 + 1] as number
			const b = colors[i * 3 + 2] as number
			greenColors.push([r, g, b])
			greenWeights.push(pixelWeights[i] as number)
			greenIndices.push(i)
			weightSum += pixelWeights[i] as number
		}
	}

	if (greenColors.length === 0) {
		return null
	}

	const center = getWeightedClusterCenter({
		clusterIndices: greenIndices,
		input: colors,
		pixelWeights,
	})

	const out: vec3 = [0, 0, 0]
	if (weightSum < 0.02) {
		logColorMatch(
			"[getVegetationColors] total weight is too low",
			weightSum,
			rgbToHsv(out, center),
		)
		return null
	}

	const greenCluster: ColorCluster = {
		center,
		colors: greenColors,
		indices: greenIndices,
		weights: greenWeights,
		weightSum,
	}

	logColorMatch("[getVegetationColors] mean green hsv", {
		meanHsv: rgbToHsv(out, center),
		count: greenColors.length,
		weightSum,
	})

	return greenCluster
}

/** Compute WB by transferring the reference's relative WB shift onto the input's as-shot WB. */
export function applyRefWb(options: {
	adjustments: Partial<Adjustments>
	asShotWB: AsShotWB
	refWb: ReferenceColorMatchFeatures["whiteBalance"]
}) {
	const { adjustments, asShotWB, refWb } = options
	if (!refWb) {
		return {
			temperature: adjustments.temperature ?? asShotWB.asShotTemp,
			tint: adjustments.tint ?? asShotWB.asShotTint,
		}
	}

	const refTempDelta = (refWb.temperature - refWb.asShotTemperature) / refWb.asShotTemperature
	const refTintDelta = refWb.tint - refWb.asShotTint

	return {
		temperature: clamp(asShotWB.asShotTemp * (1 + refTempDelta), 2000, 20000),
		tint: clamp(asShotWB.asShotTint + refTintDelta, -100, 100),
	}
}

/**
 * Correct vegetation (green/yellow) color to match the reference.
 *
 * Color match and WB correction often shift vegetation hue — typically making
 * it too yellow (warm WB) or occasionally too green (cold WB after white color
 * correction). This function pulls the vegetation color back toward the
 * reference's vegetation HSV using two complementary mechanisms:
 *
 * ### Phase 1 — WB nudge toward reference
 * If the input and reference have similar as-shot WB, blend the current WB
 * partially toward the reference's WB (strength up to 0.5, gated by WB
 * distance and colorStrength). This handles the bulk of warm/cold drift.
 * Also clamps initial tint to stay between as-shot and ref to avoid divergence.
 *
 * ### Phase 2 — HSL green/yellow channel correction
 * Extract vegetation pixels (hue 45–160°, sat > 0.2, val > 0.2), compute
 * their mean HSV before and after current adjustments, then:
 * 1. Determine target hue/sat from reference, clamped to stay near input and
 *    after values to avoid "plastic green" artifacts (hue > ~115°)
 * 2. Iteratively adjust temperature/tint (2 iterations) to close the hue gap
 * 3. Iteratively adjust hueGreen/hueYellow/satGreen/satYellow (4 iterations)
 *    to refine hue and saturation, with decreasing scale to avoid overshoot
 * 4. Blend results by `strength` (reduced when ref vege color is very
 *    different from input) and clamp to averageHSL range for consistency
 *
 * The WB adjustment range is bounded by `getWbRange` to avoid pushing WB
 * away from both as-shot and reference WB simultaneously.
 */
export function correctVegetation(options: {
	adjustments: Partial<Adjustments>
	srgbBefore: Float32Array
	pixelWeights: number[]
	asShotWB: AsShotWB
	baseRenderOptions: RenderOptions
	referenceFeatures: ReferenceColorMatchFeatures
	hasBaseAdjustments: boolean
	colorStrength?: number
	averageHSL?: Partial<Adjustments>
}): Partial<Adjustments> {
	if (!enableVegetationCorrection) {
		return options.adjustments
	}

	const {
		adjustments,
		srgbBefore,
		pixelWeights,
		asShotWB,
		baseRenderOptions,
		referenceFeatures,
		hasBaseAdjustments,
		averageHSL,
	} = options

	// if vege correction is skipped, try to correct wb to ref's wb using max strength 0.5 if wb dist is small
	const colorStrength = options.colorStrength ?? 1
	let refWbStrength = 0
	const refWbAdjustments = { ...adjustments }
	const initialTemp = adjustments.temperature ?? asShotWB.asShotTemp
	let initialTint = adjustments.tint ?? asShotWB.asShotTint
	let refWbAdjustmentsFull = { temperature: initialTemp, tint: initialTint }
	let result = { ...adjustments }
	if (hasBaseAdjustments && colorStrength > 0.9) {
		const refWb = referenceFeatures.whiteBalance
		if (refWb) {
			const dist =
				percentDifference(asShotWB.asShotTemp, refWb.asShotTemperature) +
				absoluteDifference(asShotWB.asShotTint, refWb.asShotTint, 20) / 100
			refWbStrength = clamp(
				Math.min(colorStrength * 2 - 1, 1 - step(dist, 0, 0.35)) * 0.55,
				0,
				0.5,
			)
			refWbAdjustmentsFull = applyRefWb({ adjustments, asShotWB, refWb })

			refWbAdjustments.temperature = Math.min(
				initialTemp * 1.05,
				mix(initialTemp, refWbAdjustmentsFull.temperature, refWbStrength),
			)
			refWbAdjustments.tint = Math.min(
				initialTint + 6,
				mix(initialTint, refWbAdjustmentsFull.tint, refWbStrength),
			)

			// limit tint to be not too far from asShotWB and refWbAdjustmentsFull as a better starting point
			const minTint = Math.min(
				refWbAdjustments.tint - 1,
				refWbAdjustmentsFull.tint - 3,
				asShotWB.asShotTint - 5,
			)
			const maxTint = Math.max(
				refWbAdjustments.tint + 1,
				refWbAdjustmentsFull.tint + 3,
				asShotWB.asShotTint + 5,
			)
			result.tint = clamp(initialTint, minTint, maxTint)

			logColorMatch("[correctVegetation] wbToRef", {
				refWbAdjustmentsFull,
				refWbStrength,
				dist,
				refWbResultTemp: refWbAdjustments.temperature,
				refWbResultTint: refWbAdjustments.tint,
				initialTint,
				initialTemp,
				resultTint: result.tint,
			})
			initialTint = result.tint
		}
	}

	const refVegeHsv = referenceFeatures.vegetationHsv
	if (!refVegeHsv) {
		return refWbAdjustments
	}

	// get vege cluster from srgbAutoLight instead of srgbBefore because its lighting is more standard
	const lightAdjustments = extractKeys(adjustments, lightKeys) as Partial<Adjustments>
	const srgbAutoLight = renderCpu(srgbBefore, lightAdjustments, {
		inputColorSpace: ColorSpaceCpu.sRGB,
		outColorSpace: ColorSpaceCpu.sRGB,
		...baseRenderOptions,
	})
	const vegeCluster = getVegetationCluster({
		colors: srgbAutoLight,
		pixelWeights,
	})

	if (!vegeCluster) {
		return refWbAdjustments
	}

	// get vege colors array and pixel weights array from before image
	const { colors: vegeRgbBeforeArray, pixelWeights: vegePixelWeights } =
		getClusterColorsAndWeights({
			input: srgbBefore,
			pixelWeights,
			cluster: vegeCluster,
		})
	const vegePixelWeightsArray = new Float32Array(vegePixelWeights)

	const vegeRgbAfterArray = renderCpu(vegeRgbBeforeArray, result, {
		inputColorSpace: ColorSpaceCpu.sRGB,
		outColorSpace: ColorSpaceCpu.sRGB,
		...baseRenderOptions,
	})
	const vegeRgbAfter = getWeightedMeanRgb(vegeRgbAfterArray, vegePixelWeightsArray)
	if (!vegeRgbAfter) {
		return refWbAdjustments
	}

	// get mean vege rgb and hsv before adjustments
	const vegeRgbBefore = getWeightedMeanRgb(vegeRgbBeforeArray, vegePixelWeightsArray)
	if (!vegeRgbBefore) {
		return refWbAdjustments
	}
	const hsvBefore = vec3.create()
	rgbToHsv(hsvBefore, vegeRgbBefore)
	const hueBefore = hsvBefore[0] * 360

	// get mean hsv after adjustments
	const vegeHsvAfter = vec3.create()
	rgbToHsv(vegeHsvAfter, vegeRgbAfter)
	let hueAfter = (vegeHsvAfter?.[0] ?? 0) * 360
	const hueRef = (refVegeHsv?.[0] ?? 0) * 360

	// -- important: decide the target sat now --->
	const satBefore = hsvBefore[1]
	let satAfter = vegeHsvAfter[1]
	const satRef = refVegeHsv[1]
	const maxSat = Math.max(satBefore, satAfter, 0.45)
	let targetSat = clamp(satRef, satAfter - 0.15, Math.min(satAfter + 0.1, maxSat))
	if (targetSat > satBefore && targetSat > satRef) {
		targetSat = Math.max(satBefore, satRef)
	} else if (targetSat < satBefore && targetSat < satRef) {
		targetSat = Math.min(satBefore, satRef)
	}

	// -- important: decide the target hue now --->
	// Normally our color match tends to make the image more yellow than reference
	// but with white color correction, it's possible to make the image more cold than reference

	// General idea: use reference hue as target, but consider weight according to colorStrength, input image's vege color weight, limit the target hue to be not too far from before and after, avoid plastic green color (which may from clothes not vegetation) and decrease the correction strength if the diffFromInput is too large

	// Compute target hue, limit it to be not too far from before and after
	let targetHue = hueRef * colorStrength + hueAfter * (1 - colorStrength)
	targetHue = Math.max(Math.min(targetHue, hueBefore + 15), Math.min(hueAfter, targetHue))

	// Avoid plastic green color (which may from clothes not vegetation)
	const maxTargetHue = Math.max(hueBefore, hueAfter, 110) // use Math.max because we want to respect input and after's hue, and only avoids reference hue being too large
	const maxTargetHue2 = Math.max(hueBefore, 115) // only trust input hue, drag back toward natural range
	targetHue = Math.min(targetHue, maxTargetHue, maxTargetHue2)

	// Weight strength according to input image's vege color percentage
	const weightStrength = Math.min(1, Math.max(0.3, vegeCluster.weightSum / 0.08))

	// compute hue diff (the delta we want to correct)
	let hueDiff = (targetHue - hueAfter) * weightStrength // -: adjust towards more yellow, +: adjust towards more green

	// gradually reduce correction delta if hue diff is too negative;
	// use threshold -5 because normally the hueDiff is positive; rarely the color match makes the image more green than reference
	if (hueDiff < -5) {
		hueDiff = getDecreasedValue(hueDiff, 5, 0.3)
	}

	// if hue diff is too large, decrease it
	if (hueDiff > 15) {
		hueDiff = getDecreasedValue(hueDiff, 15, 0.3)
	}

	// get the final target hue by applying the computed hue diff
	targetHue = hueAfter + hueDiff

	// ref's vege color is too different from input's vege color, decrease vege correction strength
	const diffFromInput = Math.abs(hueRef - hueBefore)
	let strength = Math.min(1, weightStrength * 1.2)
	if (diffFromInput > 15) {
		strength = Math.min(strength, clamp(1 - (diffFromInput - 15) / 20.0, 0.5, 1))
	}

	logColorMatch("[correctVegetation] hsv", {
		hsvBefore: [hueBefore, satBefore, hsvBefore[2]],
		hsvAfter: [hueAfter, satAfter, vegeHsvAfter[2]],
		refVegeHsv: [hueRef, satRef, refVegeHsv[2]],
		targetSat,
		targetHue,
	})
	logColorMatch("[correctVegetation] strength", {
		weightStrength,
		strength,
		diffFromInput,
	})

	const correctWBScale = hasBaseAdjustments ? 1 : 0.5
	if (correctWBScale > 0) {
		// first correct wb

		let currentTemp = initialTemp
		let currentTint = initialTint

		const { minTemp, maxTemp, minTint, maxTint } = getWbRange({
			adjustments: result,
			asShotWB,
			refWB: referenceFeatures.whiteBalance,
			deltaTemp: 0.015,
			deltaTint: 1,
		})
		logColorMatch("[correctVegetation] wb range", {
			minTemp,
			minTint,
			currentTemp,
			currentTint,
		})

		const tempScale = hueDiff > 0 ? 1 : 0.5
		const tintScale = hueDiff > 0 ? 0.6 : 0.2
		const vegeHsv = vec3.create()
		for (let iteration = 0; iteration < 2; iteration++) {
			const tempDelta = asShotWB.asShotTemp * (-hueDiff / 500)
			const tintDelta = -hueDiff / 3
			result.temperature = clamp(currentTemp + tempDelta * tempScale, minTemp, maxTemp)
			result.tint = clamp(currentTint + tintDelta * tintScale, minTint, maxTint)
			logColorMatch("[correctVegetation] iteration", {
				iteration,
				tempDelta,
				tintDelta,
				temperature: result.temperature,
				tint: result.tint,
				hueDiff,
			})

			const vegeRgbArray = renderCpu(vegeRgbBeforeArray, result, {
				inputColorSpace: ColorSpaceCpu.sRGB,
				outColorSpace: ColorSpaceCpu.sRGB,
				...baseRenderOptions,
			})
			const vegeRgb = getWeightedMeanRgb(vegeRgbArray, vegePixelWeightsArray)
			if (!vegeRgb) {
				return result
			}
			rgbToHsv(vegeHsv, vegeRgb)
			const hue = (vegeHsv?.[0] ?? 0) * 360
			hueDiff = targetHue - hue
			if (Math.abs(hueDiff) < 1) {
				break
			}
			currentTemp = result.temperature ?? asShotWB.asShotTemp
			currentTint = result.tint ?? asShotWB.asShotTint
		}

		result.temperature = mix(
			initialTemp,
			result.temperature ?? asShotWB.asShotTemp,
			Math.min(strength, correctWBScale),
		)
		result.tint = mix(
			initialTint,
			result.tint ?? asShotWB.asShotTint,
			Math.min(strength, correctWBScale),
		)
	}

	// correct green/yellow hsl
	const vegeHsv = vec3.create()
	if (correctWBScale > 0) {
		const vegeRgbArray = renderCpu(vegeRgbBeforeArray, result, {
			inputColorSpace: ColorSpaceCpu.sRGB,
			outColorSpace: ColorSpaceCpu.sRGB,
			...baseRenderOptions,
		})
		const vegeRgb = getWeightedMeanRgb(vegeRgbArray, vegePixelWeightsArray)
		if (!vegeRgb) {
			return result
		}
		rgbToHsv(vegeHsv, vegeRgb)
		hueAfter = vegeHsv[0] * 360
		satAfter = vegeHsv[1]
	}

	const hueAve = hueBefore * 0.6 + hueAfter * 0.4
	let greenFract = Math.max(0, Math.min(1, (hueAve - 45) / (95 - 45)))
	const yellowFract = Math.min(1, 1.2 - greenFract)
	greenFract = Math.min(1, 0.2 + greenFract)
	let finalHueDiff = getDecreasedValue(hueDiff, 30, 0.5) / 360
	let satDiff = targetSat - satAfter

	for (let iteration = 0; iteration < 4; iteration++) {
		result = correctGreenYellowHSL({
			adjustments,
			result,
			greenFract,
			yellowFract,
			satDiff,
			hueDiff: finalHueDiff,
			scale: iteration >= 2 ? 0.4 : 0.8, // gradually reduce scale to avoid overshooting
		})

		if (iteration == 3) break

		const vegeRgbArray = renderCpu(vegeRgbBeforeArray, result, {
			inputColorSpace: ColorSpaceCpu.sRGB,
			outColorSpace: ColorSpaceCpu.sRGB,
			...baseRenderOptions,
		})
		const vegeRgb = getWeightedMeanRgb(vegeRgbArray, vegePixelWeightsArray)
		if (!vegeRgb) {
			return result
		}
		rgbToHsv(vegeHsv, vegeRgb)
		logColorMatch("[correctVegetation] iteration", {
			iteration: iteration + 1,
			hueAfter: vegeHsv[0] * 360,
			targetHue,
			targetSat,
			satAfter: vegeHsv[1],
		})

		finalHueDiff = getDecreasedValue(targetHue - vegeHsv[0] * 360, 30, 0.5) / 360
		satDiff = targetSat - vegeHsv[1]
		if (Math.abs(finalHueDiff) < 0.001 && Math.abs(satDiff) < 0.01) {
			break
		}
	}

	// if (hasBaseAdjustments) {
	// 	strength = Math.min(strength, 0.5)
	// }

	result.hueGreen = mix(adjustments.hueGreen ?? 0, result.hueGreen ?? 0, strength)
	result.hueYellow = mix(adjustments.hueYellow ?? 0, result.hueYellow ?? 0, strength)

	// limit hue to be not too far from averageHSL

	if (averageHSL || hasBaseAdjustments) {
		const averageHueGreen = hasBaseAdjustments
			? (adjustments.hueGreen ?? 0)
			: (averageHSL?.hueGreen ?? 0)
		const averageHueYellow = hasBaseAdjustments
			? (adjustments.hueYellow ?? 0)
			: (averageHSL?.hueYellow ?? 0)
		const averageSatGreen = hasBaseAdjustments
			? (adjustments.saturationGreen ?? 0)
			: (averageHSL?.saturationGreen ?? 0)
		const averageSatYellow = hasBaseAdjustments
			? (adjustments.saturationYellow ?? 0)
			: (averageHSL?.saturationYellow ?? 0)
		const hueRange = hasBaseAdjustments ? 0.06 : 0.1
		result.hueGreen = clamp(
			result.hueGreen,
			averageHueGreen - hueRange,
			averageHueGreen + hueRange,
		)
		result.hueYellow = clamp(
			result.hueYellow,
			averageHueYellow - hueRange,
			averageHueYellow + hueRange,
		)
		result.saturationGreen = clamp(
			result.saturationGreen ?? averageSatGreen,
			averageSatGreen - 0.15,
			averageSatGreen + 0.15,
		)
		result.saturationYellow = clamp(
			result.saturationYellow ?? averageSatYellow,
			averageSatYellow - 0.15,
			averageSatYellow + 0.15,
		)
	}

	return result
}

/** Compute allowed WB range — encourages WB to stay between as-shot and ref WB,
 * prevents corrections from pushing WB in the opposite direction of both anchors. */
export function getWbRange(options: {
	adjustments: Partial<Adjustments>
	asShotWB: AsShotWB
	refWB: ReferenceColorMatchFeatures["whiteBalance"]
	deltaTemp?: number
	deltaTint?: number
}) {
	const { adjustments, asShotWB, refWB, deltaTemp = 0.05, deltaTint = 7 } = options

	// maximum allowed temperature / tint reduction from as-shot white balance
	let reducedTemp = -deltaTemp * 2 * asShotWB.asShotTemp // x2 because we usually need to reduce temperature more as model tends to make image warmer
	let reducedTint = -deltaTint
	let increasedTemp = deltaTemp * asShotWB.asShotTemp
	let increasedTint = deltaTint
	if (refWB) {
		const { temperature, tint } = applyRefWb({
			adjustments,
			asShotWB,
			refWb: refWB,
		})
		reducedTemp = temperature - asShotWB.asShotTemp - 200
		reducedTint = tint - asShotWB.asShotTint - 4
		increasedTemp = temperature - asShotWB.asShotTemp + 200
		increasedTint = tint - asShotWB.asShotTint + 4
	}

	const currentTemp = adjustments.temperature ?? 0
	const maxTemp = Math.max(
		Math.min(asShotWB.asShotTemp + increasedTemp, currentTemp * (1 + deltaTemp)),
		currentTemp,
		asShotWB.asShotTemp + Math.min(increasedTemp * 0.5, 0),
	)
	const minTemp = Math.min(
		Math.max(asShotWB.asShotTemp + reducedTemp, currentTemp * (1 - deltaTemp * 2)),
		currentTemp,
		asShotWB.asShotTemp + Math.max(reducedTemp * 0.5, 0),
	)

	const currentTint = adjustments.tint ?? 0
	const maxTint = Math.max(
		Math.min(asShotWB.asShotTint + increasedTint, currentTint + 8 + deltaTint),
		currentTint,
		asShotWB.asShotTint + Math.min(increasedTint * 0.5, 0),
	)
	const minTint = Math.min(
		Math.max(asShotWB.asShotTint + reducedTint, currentTint - 8 - deltaTint),
		currentTint,
		asShotWB.asShotTint + Math.max(reducedTint * 0.5, 0),
	)

	return { minTemp, maxTemp, minTint, maxTint }
}

/** Allowed hueGreen/hueYellow parameter range, asymmetric around current value. */
function getVegeHueRange(hue: number): vec2 {
	const min = Math.max(Math.min(-0.25, hue - 0.05), Math.min(-0.1, hue - 0.4))
	const max = Math.min(Math.max(0.25, hue + 0.05), Math.max(0.1, hue + 0.4))
	return [min, max]
}

/** Allowed satGreen/satYellow parameter range, asymmetric around current value. */
function getVegeSatRange(sat: number): vec2 {
	const min = Math.max(Math.min(-0.4, sat - 0.05), Math.min(-0.1, sat - 0.3))
	const max = Math.min(Math.max(0.25, sat + 0.05), Math.max(0.1, sat + 0.1))
	return [min, max]
}

/** Apply one iteration of hue/saturation correction via green and yellow HSL channels,
 * distributing the adjustment by greenFract/yellowFract and clamping to safe ranges. */
function correctGreenYellowHSL(options: {
	adjustments: Partial<Adjustments>
	result: Partial<Adjustments>
	greenFract: number
	yellowFract: number
	satDiff: number
	hueDiff: number
	scale?: number
}) {
	const { greenFract, yellowFract, satDiff, hueDiff, adjustments, scale = 1 } = options
	const result = options.result
	const hueGreenRange = getVegeHueRange(adjustments.hueGreen ?? 0)
	const satGreenRange = getVegeSatRange(adjustments.saturationGreen ?? 0)
	const hueYellowRange = getVegeHueRange(adjustments.hueYellow ?? 0)
	const satYellowRange = getVegeSatRange(adjustments.saturationYellow ?? 0)

	if (greenFract > 0) {
		const hueScale = hueShiftScaleMap["green"]
		let hueDelta = hueDiff < 0 ? hueDiff / (hueScale[0] * 0.09) : hueDiff / (hueScale[1] * 0.09)
		hueDelta = clamp(hueDelta * greenFract, -0.2, 0.3) * scale
		result.hueGreen = clamp(
			(result.hueGreen ?? 0) + hueDelta,
			hueGreenRange[0],
			hueGreenRange[1],
		)

		const satDelta = getDecreasedValue((satDiff / 0.4) * greenFract, 0.2, 0.5) * scale
		const currGreenSat = result.saturationGreen ?? 0
		result.saturationGreen = clamp(currGreenSat + satDelta, satGreenRange[0], satGreenRange[1])

		logColorMatch("[correctVegetation] green", {
			hueDelta,
			satDelta,
			greenFract,
			hueDiff,
			finalHueGreen: result.hueGreen,
		})
	}
	if (yellowFract > 0) {
		const hueScale = hueShiftScaleMap["yellow"]
		let hueDelta = hueDiff < 0 ? hueDiff / (hueScale[0] * 0.09) : hueDiff / (hueScale[1] * 0.09)
		hueDelta = clamp(hueDelta * yellowFract, -0.2, 0.3) * scale
		result.hueYellow = clamp(
			(result.hueYellow ?? 0) + hueDelta,
			hueYellowRange[0],
			hueYellowRange[1],
		)

		const currYellowSat = result.saturationYellow ?? 0
		const satDelta = getDecreasedValue((satDiff / 0.4) * yellowFract, 0.2, 0.5) * scale

		result.saturationYellow = clamp(
			currYellowSat + satDelta,
			satYellowRange[0],
			satYellowRange[1],
		)

		logColorMatch("[correctVegetation] yellow", {
			hueDelta,
			satDelta,
			yellowFract,
			hueDiff,
			finalHueYellow: result.hueYellow,
		})
	}

	return result
}
